import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { BallkidContext } from "../context/BallkidContext";
import { invalidateSchedulesCache } from "../firebaseService";
import { useGroup } from "../context/GroupContext";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";
import { getCourtStartTime, getCrewSchedule, getAvailableBallkidsForDay, getAllCourtAssignments } from "../firebaseService";
import {
  People,
  SportsTennis,
  EventNote,
  ConfirmationNumber,
  Refresh,
} from "@mui/icons-material";

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];
const CREW_LABELS = ["A", "B", "C", "D"];
const CREW_COLORS = ["#1976d2", "#388e3c", "#f57c00", "#7b1fa2"];

const StatCard = ({ icon, label, value, color, subtitle }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { color, fontSize: 24 } })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const PerDayTable = ({ daysOfWeek, ballkids, selectedDay, setSelectedDay }) => {
  const [availableCounts, setAvailableCounts] = useState({});

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      daysOfWeek.map((day) =>
        getAvailableBallkidsForDay(day).then((available) => [day, available.length])
      )
    ).then((results) => {
      if (!cancelled) {
        const counts = {};
        results.forEach(([day, count]) => { counts[day] = count; });
        setAvailableCounts(counts);
      }
    });
    return () => { cancelled = true; };
  }, [daysOfWeek]);

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 1 }}>Tag</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Anwesend</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Eingeteilt</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Courts</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Crews</TableCell>
            <TableCell align="center" sx={{ fontWeight: 600 }}>Einsaetze</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {daysOfWeek.map((day) => {
            const dayBks = ballkids.filter((bk) =>
              (bk.assignments || []).some((a) => a.day === day)
            );
            const dayTotalAssignments = ballkids.reduce(
              (sum, bk) => sum + (bk.assignments || []).filter((a) => a.day === day).length, 0
            );
            const dayCourts = new Set();
            const dayCrews = new Set();
            ballkids.forEach((bk) => {
              (bk.assignments || []).filter((a) => a.day === day).forEach((a) => {
                if (a.court) dayCourts.add(a.court);
                if (a.court && a.crew) dayCrews.add(`${a.court}_${a.crew}`);
              });
            });
            const isSelected = day === selectedDay;
            const hasData = dayBks.length > 0;
            const avail = availableCounts[day];
            return (
              <TableRow
                key={day}
                hover
                onClick={() => setSelectedDay(day)}
                sx={{
                  cursor: "pointer",
                  bgcolor: isSelected ? "rgba(25, 118, 210, 0.12)" : "transparent",
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: isSelected ? 700 : 400,
                    position: "sticky",
                    left: 0,
                    bgcolor: isSelected ? "rgba(25, 118, 210, 0.12)" : "background.paper",
                    zIndex: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {day}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={avail !== undefined ? `${avail}/${ballkids.length}` : "..."}
                    size="small"
                    color={avail > 0 ? "primary" : "default"}
                    variant={avail > 0 ? "filled" : "outlined"}
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={`${dayBks.length}/${avail !== undefined ? avail : ballkids.length}`}
                    size="small"
                    color={hasData ? "success" : "default"}
                    variant={hasData ? "filled" : "outlined"}
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: hasData ? 600 : 400, color: hasData ? "text.primary" : "text.disabled" }}>
                    {dayCourts.size}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: hasData ? 600 : 400, color: hasData ? "text.primary" : "text.disabled" }}>
                    {dayCrews.size}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: hasData ? 600 : 400, color: hasData ? "text.primary" : "text.disabled" }}>
                    {dayTotalAssignments}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const HomePage = () => {
  const { ballkids, loading, reload } = useContext(BallkidContext);
  const { activeGroup } = useGroup();
  const { daysOfWeek } = useTournamentConfig();
  const memberLabel = activeGroup?.memberLabel || "Mitglieder";

  // Default to today if it matches a tournament day
  const [selectedDay, setSelectedDay] = useDefaultTournamentDay(daysOfWeek);

  // Court start times state
  const [courtConfigs, setCourtConfigs] = useState({});
  const [flexSchedule, setFlexSchedule] = useState(null);
  const [scheduleMode, setScheduleMode] = useState("simple");
  const [loadingSchedule, setLoadingSchedule] = useState(true);

  // Court/Crew assignments for selected day, loaded in creation order from Firestore
  const [courtAssignments, setCourtAssignments] = useState({});

  // Available ballkids per day (attendance)
  const [availableCount, setAvailableCount] = useState(null);

  // Crew detail drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null); // { crew, court } or just { crew }

  const handleCrewClick = (crew, court) => {
    setSelectedCrew({ crew, court });
    setDrawerOpen(true);
  };

  const loadDaySchedule = useCallback(async (day) => {
    setLoadingSchedule(true);
    try {
      // Load court/crew assignments for this day in creation order (from courtAssignments docs)
      const rawAssignments = await getAllCourtAssignments(day);
      const byCourt = {};
      Object.entries(rawAssignments).forEach(([key, names]) => {
        // Doc ID format: "{day}_{court}_{crewX}" — day and court may contain spaces but no underscores
        const parts = key.split("_");
        if (parts.length < 3) return;
        const court = parts.slice(1, -1).join("_");
        const crewLabel = parts[parts.length - 1].replace("crew", "");
        if (!byCourt[court]) byCourt[court] = { A: [], B: [], C: [], D: [] };
        byCourt[court][crewLabel] = names;
      });
      setCourtAssignments(byCourt);

      // Load flexible schedule
      const schedule = await getCrewSchedule(day);
      if (schedule && schedule.slots && schedule.mode !== "simple") {
        setFlexSchedule(schedule);
        setScheduleMode("flexible");
        setCourtConfigs({});
      } else {
        setFlexSchedule(null);
        setScheduleMode("simple");
        // Load simple court configs
        const configs = {};
        await Promise.all(
          courts.map(async (court) => {
            const data = await getCourtStartTime(day, court);
            if (data.startTime) {
              configs[court] = {
                startTime: data.startTime,
                hasThreeCrews: data.hasThreeCrews || false,
                isTurnusEnabled: data.isTurnusEnabled || false,
              };
            }
          })
        );
        setCourtConfigs(configs);
      }
    } catch (err) {
      console.error("Error loading schedule:", err);
    }
    setLoadingSchedule(false);
  }, []);

  useEffect(() => {
    if (selectedDay) loadDaySchedule(selectedDay);
  }, [selectedDay, loadDaySchedule]);

  // Load available ballkids count for selected day
  useEffect(() => {
    if (!selectedDay) return;
    let cancelled = false;
    getAvailableBallkidsForDay(selectedDay).then((available) => {
      if (!cancelled) setAvailableCount(available.length);
    });
    return () => { cancelled = true; };
  }, [selectedDay]);

  // Filter data for selected day
  const dayAssignments = ballkids.reduce((acc, bk) => {
    const dayAsgn = (bk.assignments || []).filter((a) => a.day === selectedDay);
    if (dayAsgn.length > 0) {
      acc.push({ ...bk, dayAssignments: dayAsgn });
    }
    return acc;
  }, []);

  const totalBallkids = ballkids.length;
  const dayAssignmentCount = dayAssignments.reduce((sum, bk) => sum + bk.dayAssignments.length, 0);
  const dayAssignedBallkids = dayAssignments.length;

  // All days stats for overview
  const allDays = new Set();
  ballkids.forEach((bk) =>
    bk.assignments?.forEach((a) => allDays.add(a.day))
  );

  // Generate simple mode preview slots
  const getPreviewSlots = (config) => {
    if (!config?.startTime) return null;
    const [h, m] = config.startTime.split(":").map(Number);
    const interval = config.isTurnusEnabled ? 45 : 60;
    const totalCrews = config.hasThreeCrews ? 3 : 2;
    const slots = [];
    const current = new Date();
    current.setHours(h, m, 0, 0);
    const endTime = new Date();
    endTime.setHours(20, 0, 0, 0);
    while (current <= endTime) {
      const hh = String(current.getHours()).padStart(2, "0");
      const mm = String(current.getMinutes()).padStart(2, "0");
      slots.push(`${hh}:${mm}`);
      current.setMinutes(current.getMinutes() + interval);
    }
    return { slots, totalCrews };
  };

  // Timeline for flexible mode
  const timeToMinutes = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const getFlexTimeline = () => {
    if (!flexSchedule?.slots) return null;
    const allSlots = [];
    Object.entries(flexSchedule.slots).forEach(([crew, slots]) => {
      (slots || []).forEach((slot) => allSlots.push({ crew, ...slot }));
    });
    if (allSlots.length === 0) return null;

    const times = new Set();
    allSlots.forEach((s) => { times.add(s.from); times.add(s.to); });
    const sortedTimes = Array.from(times).sort();
    const [eh, em] = sortedTimes[0].split(":").map(Number);
    const [lh, lm] = sortedTimes[sortedTimes.length - 1].split(":").map(Number);

    const timeline = [];
    let cur = eh * 60 + em;
    const end = lh * 60 + lm;
    while (cur < end) {
      const hh = String(Math.floor(cur / 60)).padStart(2, "0");
      const mm = String(cur % 60).padStart(2, "0");
      timeline.push(`${hh}:${mm}`);
      cur += 30;
    }
    return { timeline, allSlots, crewCount: flexSchedule.crewCount || 2 };
  };

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">Daten werden geladen...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeGroup?.name || "BMW Open by bitpanda 2026"}
          </Typography>
        </Box>
        <Tooltip title="Daten neu laden">
          <IconButton
            onClick={() => { invalidateSchedulesCache(); reload(); }}
            sx={{ mt: 0.5 }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Tag-Auswahl */}
      <Tabs
        value={selectedDay}
        onChange={(_, v) => setSelectedDay(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {daysOfWeek.map((day) => (
          <Tab key={day} label={day} value={day} />
        ))}
      </Tabs>

      {/* Stats fuer ausgewaehlten Tag */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<People />}
            label={`Anwesend / ${memberLabel}`}
            value={availableCount !== null ? `${availableCount}/${totalBallkids}` : totalBallkids}
            color="#29b6f6"
            subtitle={`am ${selectedDay}`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<SportsTennis />}
            label="Einteilungen"
            value={dayAssignmentCount}
            color="#5b8def"
            subtitle={`am ${selectedDay}`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<EventNote />}
            label="Eingeteilt"
            value={`${dayAssignedBallkids}/${availableCount !== null ? availableCount : totalBallkids}`}
            color="#66bb6a"
            subtitle={`am ${selectedDay}`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon={<ConfirmationNumber />}
            label="Courts besetzt"
            value={`${Object.keys(courtAssignments).length}/${courts.length}`}
            color="#ffa726"
            subtitle={`am ${selectedDay}`}
          />
        </Grid>
      </Grid>

      {/* Einteilungsstatistiken pro Tag */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Einteilungen pro Tag
          </Typography>
          <PerDayTable
            daysOfWeek={daysOfWeek}
            ballkids={ballkids}
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
          />
        </CardContent>
      </Card>

      {/* Court-Einsatzzeiten fuer den Tag */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Einsatzzeiten - {selectedDay}
          </Typography>
          {loadingSchedule ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : scheduleMode === "flexible" && flexSchedule ? (
            // Flexible mode timeline
            (() => {
              const preview = getFlexTimeline();
              if (!preview) return <Typography color="text.secondary" variant="body2">Kein Crew-Plan konfiguriert.</Typography>;
              return (
                <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflow: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 2, minWidth: 70 }}>Crew</TableCell>
                        {preview.timeline.map((t) => (
                          <TableCell key={t} align="center" sx={{ minWidth: 55, fontSize: "0.75rem", fontWeight: 600 }}>{t}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {CREW_LABELS.slice(0, preview.crewCount).map((crew, crewIdx) => (
                        <TableRow key={crew}>
                          <TableCell sx={{ fontWeight: 700, color: CREW_COLORS[crewIdx], position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 1 }}>
                            Crew {crew}
                          </TableCell>
                          {preview.timeline.map((t) => {
                            const tMin = timeToMinutes(t);
                            const slot = preview.allSlots.find(
                              (s) => s.crew === crew && timeToMinutes(s.from) <= tMin && timeToMinutes(s.to) > tMin
                            );
                            return (
                              <TableCell
                                key={t}
                                align="center"
                                sx={{
                                  fontSize: "0.65rem",
                                  bgcolor: slot ? CREW_COLORS[crewIdx] : "transparent",
                                  color: slot ? "white" : "text.disabled",
                                  py: 0.5,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {slot ? slot.court.replace("Court ", "C") : "---"}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              );
            })()
          ) : Object.keys(courtConfigs).length > 0 ? (
            // Simple mode - court cards
            <Grid container spacing={2}>
              {Object.entries(courtConfigs).map(([court, config]) => {
                const preview = getPreviewSlots(config);
                if (!preview) return null;
                return (
                  <Grid item xs={12} md={6} key={court}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{court}</Typography>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Chip label={`Start: ${config.startTime}`} size="small" variant="outlined" />
                          <Chip label={config.hasThreeCrews ? "3 Crews" : "2 Crews"} size="small" variant="outlined" />
                          <Chip label={config.isTurnusEnabled ? "45min" : "60min"} size="small" variant="outlined" />
                        </Box>
                      </Box>
                      <TableContainer sx={{ overflow: "auto" }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ py: 0.3, fontSize: "0.7rem", fontWeight: 600, position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 1 }}>Crew</TableCell>
                              {preview.slots.map((t) => (
                                <TableCell key={t} sx={{ py: 0.3, fontSize: "0.7rem", whiteSpace: "nowrap" }}>{t}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Array.from({ length: preview.totalCrews }, (_, i) => (
                              <TableRow key={i}>
                                <TableCell sx={{ py: 0.3, fontSize: "0.7rem", fontWeight: 600, color: CREW_COLORS[i], position: "sticky", left: 0, bgcolor: "background.paper", zIndex: 1 }}>
                                  {CREW_LABELS[i]}
                                </TableCell>
                                {preview.slots.map((_, slotIdx) => (
                                  <TableCell
                                    key={slotIdx}
                                    sx={{
                                      py: 0.3,
                                      fontSize: "0.65rem",
                                      bgcolor: slotIdx % preview.totalCrews === i ? CREW_COLORS[i] : "transparent",
                                      color: slotIdx % preview.totalCrews === i ? "white" : "text.disabled",
                                      textAlign: "center",
                                    }}
                                  >
                                    {slotIdx % preview.totalCrews === i ? "On" : "-"}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography color="text.secondary" variant="body2">
              Keine Einsatzzeiten fuer {selectedDay} konfiguriert.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Einteilungen pro Court fuer den Tag */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Einteilungen - {selectedDay}
          </Typography>
          {Object.keys(courtAssignments).length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              Noch keine Einteilungen fuer {selectedDay} vorhanden.
            </Typography>
          ) : (
            Object.entries(courtAssignments).map(([court, crews]) => (
              <Box key={court} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{court}</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
                        {CREW_LABELS.map((crew, idx) => {
                          const members = crews[crew] || [];
                          if (members.length === 0) return null;
                          return (
                            <TableCell key={crew} sx={{ fontWeight: 600, color: CREW_COLORS[idx] }}>
                              Crew {crew} ({members.length})
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.from({ length: Math.max(...CREW_LABELS.map((c) => (crews[c] || []).length), 0) }, (_, i) => (
                        <TableRow key={i}>
                          <TableCell sx={{ color: "text.secondary", fontWeight: 600 }}>{i + 1}</TableCell>
                          {CREW_LABELS.map((crew, idx) => {
                            const members = crews[crew] || [];
                            if (members.length === 0) return null;
                            return (
                              <TableCell key={crew} sx={{ fontSize: "0.85rem" }}>
                                {members[i] || ""}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {/* Crew Detail Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "80vh",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drag handle */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "rgba(255,255,255,0.3)" }} />
          </Box>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Crew-Einteilungen - {selectedDay}
          </Typography>

          {courts.map((court) => {
            const crews = courtAssignments[court];
            if (!crews) return null;
            const hasMembers = CREW_LABELS.some((c) => (crews[c] || []).length > 0);
            if (!hasMembers) return null;

            return (
              <Box key={court} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {court}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {CREW_LABELS.map((crew, idx) => {
                  const members = crews[crew] || [];
                  if (members.length === 0) return null;
                  return (
                    <Box key={crew} sx={{ mb: 1 }}>
                      <Chip
                        label={`Crew ${crew}`}
                        size="small"
                        sx={{
                          bgcolor: CREW_COLORS[idx],
                          color: "white",
                          fontWeight: 600,
                          mb: 0.5,
                          ...(selectedCrew?.crew === crew && selectedCrew?.court === court
                            ? { outline: "2px solid white" }
                            : {}),
                        }}
                      />
                      <List dense disablePadding>
                        {members.map((name) => (
                          <ListItem key={name} sx={{ py: 0.2, pl: 2 }}>
                            <ListItemText
                              primary={name}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  );
                })}
              </Box>
            );
          })}

          {Object.keys(courtAssignments).length === 0 && (
            <Typography color="text.secondary" variant="body2">
              Keine Einteilungen fuer {selectedDay}.
            </Typography>
          )}
        </Box>
      </SwipeableDrawer>
    </Box>
  );
};

export default HomePage;
